import { useMemo, useRef } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import type { ListEncountersByPatientParams } from "@elation/api-client/src/stitch-web";
import { useListEncountersByPatient } from "@elation/api-client/src/stitch-web";

import { EncounterList } from "@src/shared/components/data/EncounterList";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { navigation } from "@src/shared/navigation";

const PAGE_SIZE = 10;

export function meta() {
  return [
    { title: "Past Encounters" },
    { name: "description", content: "Patient past encounters list" },
  ];
}

type SortBy = "startedAt" | "physician";
type Order = "asc" | "desc";

function parseSortBy(raw: string | null): SortBy {
  return raw === "physician" ? "physician" : "startedAt";
}

function parseOrder(raw: string | null): Order {
  return raw === "asc" ? "asc" : "desc";
}

export default function PatientEncountersRoute() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const patientIdStr = params.patientId;
  const patientIdNum = Number(patientIdStr);
  const isValidPatientId =
    typeof patientIdStr === "string" &&
    patientIdStr.length > 0 &&
    Number.isFinite(patientIdNum) &&
    patientIdNum > 0;

  const cursor = searchParams.get("cursor") ?? undefined;
  const sortBy = parseSortBy(searchParams.get("sortBy"));
  const order = parseOrder(searchParams.get("order"));

  // Client-side cursor stack — each entry is the cursor that was active when the user hit "Next".
  // Pushing on Next and popping on Previous mirrors the PatientSearch pattern.
  const cursorStackRef = useRef<Array<string | undefined>>([]);

  const queryParams: ListEncountersByPatientParams = {
    cursor,
    limit: PAGE_SIZE,
    sortBy,
    order,
  };

  // Hook is always called; the generated hook is disabled when patientId is falsy (0/NaN).
  const query = useListEncountersByPatient(
    isValidPatientId ? patientIdNum : 0,
    queryParams,
  );

  const responseData = query.data?.data;
  const items = useMemo(() => responseData?.items ?? [], [responseData]);

  if (!isValidPatientId) {
    return <Navigate to={navigation.dashboardNotFound()} />;
  }

  const handleSortChange = (nextSortBy: SortBy, nextOrder: Order) => {
    cursorStackRef.current = [];
    const next = new URLSearchParams(searchParams);
    next.set("sortBy", nextSortBy);
    next.set("order", nextOrder);
    next.delete("cursor");
    setSearchParams(next);
  };

  const handleNextPage = () => {
    if (!responseData?.nextCursor) return;
    cursorStackRef.current.push(cursor);
    const next = new URLSearchParams(searchParams);
    next.set("cursor", responseData.nextCursor);
    setSearchParams(next);
  };

  const handlePrevPage = () => {
    const prev = cursorStackRef.current.pop();
    const next = new URLSearchParams(searchParams);
    if (prev) {
      next.set("cursor", prev);
    } else {
      next.delete("cursor");
    }
    setSearchParams(next);
  };

  const handleEncounterSelect = (encounterId: number) => {
    navigate(
      navigation.patientEncounter(patientIdStr as string, String(encounterId)),
    );
  };

  return (
    <VerticalFlexbox
      gap="md"
      className="w-full px-4 py-6 md-stitch:px-8 md-stitch:py-8"
    >
      <EncounterList
        encounters={items}
        isLoading={query.isLoading}
        isError={query.isError}
        sortBy={sortBy}
        order={order}
        onSortChange={handleSortChange}
        hasNextPage={Boolean(responseData?.nextCursor)}
        hasPrevPage={cursorStackRef.current.length > 0}
        onNextPage={handleNextPage}
        onPrevPage={handlePrevPage}
        onEncounterSelect={handleEncounterSelect}
      />
    </VerticalFlexbox>
  );
}
